import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { getIsFieldRequired } from 'utils';

import { Select } from '@atlas/ui';

import Field from 'common/Form/Field';

const SelectField = ({ field, onBlur, onChange, onFocus }) => {
  const {
    active,
    autofocus,
    disabled,
    errors,
    isModifiable = true,
    label,
    name,
    options: initialOptions,
    rules,
    touched,
    translateOptions = true,
    value,
    warnings,
  } = field;
  const initialValue = useRef(value);
  const areFieldsAsync = useMemo(() => typeof initialOptions === 'function', [initialOptions]);
  const [options, setOptions] = useState(areFieldsAsync ? [] : initialOptions);

  useEffect(() => {
    if (areFieldsAsync) {
      (async () => {
        const loadedOptions = await initialOptions();

        setOptions(loadedOptions);
      })();
    }
  }, [areFieldsAsync, initialOptions]);

  useEffect(() => {
    if (!areFieldsAsync && initialOptions !== options) {
      setOptions(initialOptions);
    }
  }, [areFieldsAsync, initialOptions, options]);

  const error = touched && errors[0];

  const changeHandler = useCallback(
    val => {
      if (initialValue.current === '<not-selected>' && val === '<not-selected>') {
        onChange(null);
      } else {
        onChange(
          val,
          true, // This parameter is required to handle touched state for select
        );
      }
    },
    [onChange],
  );

  useEffect(() => {
    if (initialValue.current === '<not-selected>' && value === '<not-selected>') {
      changeHandler(null);
    }
  }, [changeHandler, value]);

  const isRequiredSymbol = useMemo(() => getIsFieldRequired(rules), [rules]);

  const sortedOptions = useMemo(
    () => [...options].sort((a, b) => a.text.localeCompare(b.text)),
    [options],
  );

  const shouldHaveNullValue = !!sortedOptions.find(option => option.value === '<not-selected>');

  const displayOptions = useMemo(() => {
    const selectedOption = sortedOptions.find(o => o.value === value);

    return selectedOption
      ? [
          ...(initialValue.current === '<not-selected>'
            ? [
                {
                  attributes: [],
                  disabled: true,
                  isActive: true,
                  text: 'not_selected',
                  value: '<not-selected>',
                },
              ]
            : []),
          selectedOption,
          ...sortedOptions.filter(o => o.value !== value),
        ]
      : [
          ...(initialValue.current === '<not-selected>'
            ? [
                {
                  attributes: [],
                  disabled: true,
                  isActive: true,
                  text: 'not_selected',
                  value: '<not-selected>',
                },
              ]
            : []),
          ...sortedOptions,
        ];
  }, [sortedOptions, value]);

  return (
    <Field options={field}>
      <Select
        active={active}
        autofocus={autofocus}
        disabled={disabled || !isModifiable}
        error={error}
        isRequiredSymbol={isRequiredSymbol}
        label={label}
        name={name}
        onBlur={onBlur}
        onChange={changeHandler}
        onFocus={onFocus}
        options={displayOptions}
        touched={touched}
        translateOptions={translateOptions}
        value={shouldHaveNullValue && value === null ? '<not-selected>' : value}
        warnings={warnings}
      />
    </Field>
  );
};

SelectField.propTypes = {
  field: PropTypes.object.isRequired,
  onBlur: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
};

export default SelectField;
